import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiService from '../utils/api';
import './AdminPanel.css';

function AdminPanel() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('users');
  const [activeSubTab, setActiveSubTab] = useState('search');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Salary slip search states
  const [searchNik, setSearchNik] = useState('');
  const [searchYear, setSearchYear] = useState('all');
  const [salarySlips, setSalarySlips] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [searchPerformed, setSearchPerformed] = useState(false);
  
  // Upload states
  const [uploadType, setUploadType] = useState('single');
  const [uploadNik, setUploadNik] = useState('');
  const [uploadMonth, setUploadMonth] = useState(new Date().getMonth() + 1);
  const [uploadYear, setUploadYear] = useState(new Date().getFullYear());
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [updateExistingUsers, setUpdateExistingUsers] = useState('preserve');
  const [handleDuplicates, setHandleDuplicates] = useState('skip');
  const [uploadMessage, setUploadMessage] = useState('');
  const [uploadStatus, setUploadStatus] = useState('');
  const [uploadResult, setUploadResult] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  
  // Month names for display
  const monthNames = [
    'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
    'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
  ];
  
  // Add at the beginning of the component
  const [uploadTabActive, setUploadTabActive] = useState(false);
  
  // Check authentication and load data
  useEffect(() => {
    const checkAuthAndLoadData = async () => {
      if (!ApiService.isAuthenticated()) {
        navigate('/');
        return;
      }

      // Get user data
      const userData = ApiService.getCurrentUser();
      
      // Check if user is admin
      if (!userData || !userData.isAdmin) {
        navigate('/dashboard');
        return;
      }
      
      setUser(userData);
      
      // Check for URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const tabParam = urlParams.get('tab');
      
      // Set active tab based on URL parameters
      if (tabParam) {
        switch (tabParam) {
          case 'upload':
            setActiveTab('salary');
            setActiveSubTab('upload');
            setUploadTabActive(true);
            break;
          case 'users':
            setActiveTab('users');
            setUploadTabActive(false);
            break;
          case 'reports':
            setActiveTab('reports');
            setUploadTabActive(false);
            break;
          case 'settings':
            setActiveTab('settings');
            setUploadTabActive(false);
            break;
          default:
            // Default to current activeTab
            break;
        }
      }
      
      // Load users data for admin panel if on users tab
      if (tabParam === 'users' || (!tabParam && activeTab === 'users')) {
        try {
          const response = await ApiService.getAllUsers();
          setUsers(response.data || []);
        } catch (error) {
          setError('Gagal memuat data pengguna');
          console.error(error);
        }
      }
      
      setIsLoading(false);
    };
    
    checkAuthAndLoadData();
  }, [navigate]);
  
  // Filter users based on search query
  const filteredUsers = users.filter(user => 
    user.username?.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
    user.name?.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
    user.department?.toLowerCase().includes(userSearchQuery.toLowerCase())
  );

  const handleLogout = () => {
    // Clear authentication data
    ApiService.logout();
    
    // Add a small delay to ensure state is cleared properly
    setTimeout(() => {
      // Force redirect to login page
      window.location.href = '/';
    }, 100);
  };
  
  const handleDeleteUser = async (id) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus pengguna ini?')) {
      try {
        await ApiService.deleteUser(id);
        // Refresh users list
        const response = await ApiService.getAllUsers();
        setUsers(response.data || []);
      } catch (error) {
        alert('Gagal menghapus pengguna');
        console.error(error);
      }
    }
  };
  
  const handleSearchSalarySlips = async () => {
    if (!searchNik.trim()) {
      setSearchError('Silakan masukkan NIK karyawan');
      return;
    }
    
    setIsSearching(true);
    setSearchError('');
    setSalarySlips([]);
    setSearchPerformed(true);
    
    try {
      const response = await ApiService.getDocuments(
        searchNik, 
        searchYear !== 'all' ? searchYear : null
      );
      
      const documents = response.documents || [];
      
      // Process documents to extract month and year from file_name if needed
      const processedDocuments = documents.map(doc => {
        // If doc already has month and year fields, just return it
        if (doc.month && doc.year) {
          return doc;
        }
        
        // Extract month and year from file_name
        let month = '';
        let year = '';
        
        if (doc.file_name) {
          // Pattern like: ht/slipgaji/2505/1215_05_2025.pdf
          const parts = doc.file_name.split('/');
          if (parts.length >= 3) {
            const fileNamePart = parts[parts.length - 1];
            const fileNameParts = fileNamePart.split('_');
            if (fileNameParts.length >= 3) {
              month = fileNameParts[1];
              year = fileNameParts[2].split('.')[0];
            }
          }
        }
        
        return {
          ...doc,
          month: month,
          year: year
        };
      });
      
      // Sort documents by year and month (most recent first)
      processedDocuments.sort((a, b) => {
        if (a.year !== b.year) {
          return b.year - a.year; // Descending by year
        }
        return b.month - a.month; // Descending by month
      });
      
      setSalarySlips(processedDocuments);
    } catch (error) {
      console.error('Error searching salary slips:', error);
      setSearchError(error.message || 'Terjadi kesalahan saat mencari slip gaji');
    } finally {
      setIsSearching(false);
    }
  };
  
  const handleResetSearch = () => {
    setSearchNik('');
    setSearchYear('all');
    setSalarySlips([]);
    setSearchError('');
    setSearchPerformed(false);
  };
  
  const handleViewSalarySlip = async (doc) => {
    try {
      // Use file_name directly if available, otherwise construct it
      const fileName = doc.file_name || 
        `ht/slipgaji/${doc.year}${doc.month}/${doc.nik}_${doc.month}_${doc.year}.pdf`;
      
      // Call viewSalarySlip which will handle opening the PDF in a new tab
      await ApiService.viewSalarySlip(fileName);
    } catch (error) {
      console.error('Error viewing salary slip:', error);
      alert('Gagal membuka slip gaji: ' + (error.message || ''));
    }
  };

  // Group documents by year for display
  const documentsByYear = {};
  if (salarySlips.length > 0) {
    salarySlips.forEach(doc => {
      if (!documentsByYear[doc.year]) {
        documentsByYear[doc.year] = [];
      }
      documentsByYear[doc.year].push(doc);
    });
  }

  // Add these upload methods after the handleViewSalarySlip method
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setUploadFile(e.target.files[0]);
      setUploadMessage('');
      setUploadStatus('');
    }
  };

  const handleFilesChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setUploadFiles(Array.from(e.target.files));
      setUploadMessage('');
      setUploadStatus('');
    }
  };

  const validateUpload = () => {
    if (uploadType === 'single') {
      if (!uploadFile) {
        setUploadMessage('Pilih file PDF untuk diunggah');
        setUploadStatus('error');
        return false;
      }
      
      if (uploadFile.type !== 'application/pdf') {
        setUploadMessage('Hanya file PDF yang diperbolehkan');
        setUploadStatus('error');
        return false;
      }
      
      if (!uploadNik) {
        setUploadMessage('NIK karyawan diperlukan');
        setUploadStatus('error');
        return false;
      }
    } else {
      if (uploadFiles.length === 0) {
        setUploadMessage('Pilih setidaknya satu file PDF untuk diunggah');
        setUploadStatus('error');
        return false;
      }
      
      const nonPdfFile = uploadFiles.find(file => file.type !== 'application/pdf');
      if (nonPdfFile) {
        setUploadMessage(`File "${nonPdfFile.name}" bukan PDF. Hanya file PDF yang diperbolehkan`);
        setUploadStatus('error');
        return false;
      }
    }
    
    return true;
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateUpload()) {
      return;
    }
    
    setIsUploading(true);
    setUploadMessage('Mengunggah slip gaji...');
    setUploadStatus('');
    setUploadResult(null);
    
    try {
      if (uploadType === 'single') {
        // Single file upload
        const formData = new FormData();
        formData.append('file', uploadFile);
        formData.append('nik', uploadNik);
        formData.append('month', uploadMonth);
        formData.append('year', uploadYear);
        formData.append('updateExistingUsers', updateExistingUsers);
        formData.append('handleDuplicates', handleDuplicates);
        
        // Add additional metadata fields
        formData.append('filename', uploadFile.name);
        formData.append('file_type', uploadFile.type);
        formData.append('file_size', uploadFile.size);
        formData.append('content_type', 'application/pdf');
        
        const result = await ApiService.uploadSalarySlip(formData);
        
        setUploadMessage('Slip gaji berhasil diunggah');
        setUploadStatus('success');
        setUploadResult(result);
        
        // Reset file input
        setUploadFile(null);
        document.getElementById('uploadFile').value = '';
      } else {
        // Multiple files upload
        setUploadMessage('Mengunggah beberapa file...');
        
        // Track results
        const results = {
          total: uploadFiles.length,
          successful: 0,
          failed: 0,
          details: []
        };
        
        // Upload each file
        for (let i = 0; i < uploadFiles.length; i++) {
          const file = uploadFiles[i];
          
          try {
            // Try to extract NIK, month, and year from filename
            // Expected format: NIK_MONTH_YEAR.pdf (e.g., 1234_05_2024.pdf)
            let fileNik = '';
            let fileMonth = '';
            let fileYear = '';
            
            const nameParts = file.name.split('_');
            if (nameParts.length >= 3) {
              fileNik = nameParts[0];
              fileMonth = nameParts[1];
              fileYear = nameParts[2].split('.')[0];
            } else {
              // If filename doesn't follow the pattern, try to extract NIK from the filename
              // For files named just with NIK like "10016.pdf"
              const fileName = file.name.split('.')[0];
              // Check if the file name could be a NIK (numeric)
              if (/^\d+$/.test(fileName)) {
                fileNik = fileName;
                fileMonth = uploadMonth;
                fileYear = uploadYear;
              } else {
                // If not numeric, use the form values
                fileNik = ''; // We'll need manual matching later
                fileMonth = uploadMonth;
                fileYear = uploadYear;
              }
            }
            
            // For multiple uploads without clear NIK, we need to handle differently
            if (!fileNik) {
              results.failed++;
              results.details.push({
                file: file.name,
                status: 'error',
                message: 'NIK tidak dapat diekstrak dari nama file. Format: NIK_BULAN_TAHUN.pdf'
              });
              continue;
            }
            
            const formData = new FormData();
            formData.append('file', file);
            formData.append('nik', fileNik);
            formData.append('month', fileMonth);
            formData.append('year', fileYear);
            formData.append('updateExistingUsers', updateExistingUsers);
            formData.append('handleDuplicates', handleDuplicates);
            
            // Add additional metadata fields
            formData.append('filename', file.name);
            formData.append('file_type', file.type);
            formData.append('file_size', file.size);
            formData.append('content_type', 'application/pdf');
            
            // Update progress message
            setUploadMessage(`Mengunggah file ${i + 1}/${uploadFiles.length}: ${file.name}...`);
            
            const result = await ApiService.uploadSalarySlip(formData);
            
            results.successful++;
            results.details.push({
              file: file.name,
              status: 'success',
              nik: fileNik,
              month: fileMonth,
              year: fileYear
            });
          } catch (error) {
            results.failed++;
            results.details.push({
              file: file.name,
              status: 'error',
              message: error.message || 'Gagal mengunggah file'
            });
          }
          
          // Add a small delay between uploads to avoid overwhelming the server
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Update progress message
          setUploadMessage(`Mengunggah file ${i + 1}/${uploadFiles.length}...`);
        }
        
        setUploadMessage(`Selesai: ${results.successful} berhasil, ${results.failed} gagal dari total ${results.total} file`);
        setUploadStatus(results.failed === 0 ? 'success' : 'warning');
        setUploadResult(results);
        
        // Reset file input
        setUploadFiles([]);
        document.getElementById('uploadFiles').value = '';
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadMessage(`Gagal mengunggah: ${error.message || 'Terjadi kesalahan'}`);
      setUploadStatus('error');
    } finally {
      setIsUploading(false);
    }
  };

  if (isLoading) {
    return <div className="loading-screen">Loading...</div>;
  }

  return (
    <div className="admin-page">
      <header className="dashboard-header">
        <div className="container header-container">
          <div className="logo">
            <img src="/logo.png" alt="Company Logo" />
            <h1>Sistem Penggajian - Admin</h1>
          </div>
          <nav className="main-nav">
            <ul>
              <li><a href="/dashboard">Dashboard</a></li>
              <li className="active"><a href="/admin">Admin Panel</a></li>
              <li><a href="/salary">Slip Gaji</a></li>
              <li><a href="/change-password">Ubah Password</a></li>
            </ul>
          </nav>
          <div className="user-menu">
            <span className="username">{user ? user.name : 'Admin'}</span>
            <button 
              className="logout-btn"
              onClick={handleLogout}
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="admin-content">
        <div className="container">
          <div className="admin-panel">
            <div className="admin-tabs">
              <button 
                className={`tab-btn ${activeTab === 'users' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('users');
                  setUploadTabActive(false);
                }}
              >
                Manajemen User
              </button>
              <button 
                className={`tab-btn ${activeTab === 'salary' && !uploadTabActive ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('salary');
                  setActiveSubTab('search'); // Default to search when changing to salary tab
                  setUploadTabActive(false);
                }}
              >
                Cari Slip Gaji
              </button>
              <button 
                className={`tab-btn upload-tab-btn ${uploadTabActive ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('salary');
                  setActiveSubTab('upload'); // Switch to upload sub-tab
                  setUploadTabActive(true);
                }}
              >
                Unggah Slip Gaji
              </button>
              <button 
                className={`tab-btn ${activeTab === 'reports' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('reports');
                  setUploadTabActive(false);
                }}
              >
                Laporan
              </button>
              <button 
                className={`tab-btn ${activeTab === 'settings' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('settings');
                  setUploadTabActive(false);
                }}
              >
                Pengaturan
              </button>
            </div>
            
            <div className="admin-tab-content">
              {error && <div className="alert alert-danger">{error}</div>}
              
              {activeTab === 'users' && (
                <div className="users-management">
                  <div className="panel-header">
                    <h2>Manajemen User</h2>
                    <div className="panel-actions">
                      <div className="search-container">
                        <input 
                          type="text" 
                          placeholder="Cari user..." 
                          value={userSearchQuery}
                          onChange={(e) => setUserSearchQuery(e.target.value)}
                          className="search-input"
                        />
                      </div>
                      <button 
                        className="btn btn-upload"
                        onClick={() => {
                          setActiveTab('salary');
                          setActiveSubTab('upload');
                          setUploadTabActive(true);
                        }}
                      >
                        ⬆️ Unggah Slip Gaji
                      </button>
                      <button 
                        className="btn btn-primary" 
                        onClick={() => navigate('/admin/users/add')}
                      >
                        + Tambah User
                      </button>
                    </div>
                  </div>
                  
                  <div className="table-responsive">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Username</th>
                          <th>Nama</th>
                          <th>Role</th>
                          <th>Departemen</th>
                          <th>Status</th>
                          <th>Aksi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUsers.length > 0 ? (
                          filteredUsers.map(user => (
                            <tr key={user.id}>
                              <td>{user.id}</td>
                              <td>{user.username}</td>
                              <td>{user.name}</td>
                              <td>{user.role}</td>
                              <td>{user.department}</td>
                              <td>
                                <span className={`status-badge ${user.status.toLowerCase()}`}>
                                  {user.status}
                                </span>
                              </td>
                              <td>
                                <div className="action-buttons">
                                  <button 
                                    className="action-btn edit"
                                    onClick={() => navigate(`/admin/users/edit/${user.id}`)}
                                  >
                                    Edit
                                  </button>
                                  <button 
                                    className="action-btn delete"
                                    onClick={() => handleDeleteUser(user.id)}
                                  >
                                    Hapus
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="7" className="no-data">
                              {userSearchQuery ? 'Tidak ada pengguna yang sesuai dengan pencarian' : 'Tidak ada data pengguna'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              {activeTab === 'salary' && (
                <div className="salary-data">
                  <h2>Data Gaji</h2>
                  
                  <div className="sub-tabs">
                    <button 
                      className={`sub-tab-btn ${activeSubTab === 'search' ? 'active' : ''}`}
                      onClick={() => setActiveSubTab('search')}
                    >
                      Cari Slip Gaji
                    </button>
                    <button 
                      className={`sub-tab-btn ${activeSubTab === 'upload' ? 'active' : ''}`}
                      onClick={() => setActiveSubTab('upload')}
                    >
                      Unggah Slip Gaji
                    </button>
                  </div>
                  
                  {activeSubTab === 'search' && (
                    <>
                      <div className="search-form">
                        <div className="form-row">
                          <div className="form-group">
                            <label htmlFor="searchNik">NIK Karyawan:</label>
                            <input 
                              type="text" 
                              id="searchNik" 
                              placeholder="Masukkan NIK karyawan" 
                              className="form-control"
                              value={searchNik}
                              onChange={(e) => setSearchNik(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && handleSearchSalarySlips()}
                            />
                          </div>
                          <div className="form-group">
                            <label htmlFor="searchYear">Tahun:</label>
                            <select 
                              id="searchYear" 
                              className="form-control"
                              value={searchYear}
                              onChange={(e) => setSearchYear(e.target.value)}
                            >
                              <option value="all">Semua Tahun</option>
                              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                                <option key={year} value={year}>{year}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="search-actions">
                          <button 
                            className="btn-search"
                            onClick={handleSearchSalarySlips}
                            disabled={isSearching}
                          >
                            {isSearching ? 'Mencari...' : 'Cari Slip Gaji'}
                          </button>
                          <button 
                            className="btn-reset"
                            onClick={handleResetSearch}
                            disabled={isSearching}
                          >
                            Reset
                          </button>
                        </div>
                      </div>
                      
                      <div className="documents-list">
                        {searchError && <p className="error-message">{searchError}</p>}
                        
                        {isSearching && <p>Mencari slip gaji...</p>}
                        
                        {!isSearching && searchPerformed && salarySlips.length === 0 && !searchError && (
                          <p className="no-results">
                            Tidak ditemukan slip gaji untuk NIK {searchNik}
                            {searchYear !== 'all' ? ` tahun ${searchYear}` : ''}.
                          </p>
                        )}
                        
                        {!isSearching && !searchPerformed && !searchError && (
                          <p className="no-results">
                            Silakan masukkan NIK karyawan dan pilih tahun untuk mencari slip gaji.
                          </p>
                        )}
                        
                        {!isSearching && salarySlips.length > 0 && (
                          <>
                            <h4>Ditemukan {salarySlips.length} slip gaji untuk NIK {searchNik}</h4>
                            
                            {Object.keys(documentsByYear).sort((a, b) => b - a).map(year => (
                              <div key={year}>
                                <h4>Tahun {year}</h4>
                                
                                {documentsByYear[year].map((doc, index) => (
                                  <div className="document-item" key={`${doc.nik}_${doc.month}_${doc.year}`}>
                                    <div className="document-details">
                                      <strong>Slip Gaji:</strong> {monthNames[parseInt(doc.month) - 1]} {doc.year}
                                      <div><small>NIK: {doc.nik}</small></div>
                                    </div>
                                    <div className="document-actions">
                                      <button 
                                        className="btn-view"
                                        onClick={() => handleViewSalarySlip(doc)}
                                      >
                                        Lihat
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    </>
                  )}
                  
                  {activeSubTab === 'upload' && (
                    <div className="upload-section">
                      <div className="upload-form">
                        <form onSubmit={handleUploadSubmit}>
                          <div className="form-group">
                            <label htmlFor="uploadType">Tipe Unggahan:</label>
                            <select 
                              id="uploadType" 
                              className="form-control"
                              value={uploadType}
                              onChange={(e) => setUploadType(e.target.value)}
                            >
                              <option value="single">File Tunggal</option>
                              <option value="multiple">Beberapa File</option>
                            </select>
                          </div>
                          
                          {uploadType === 'single' ? (
                            <>
                              <div className="form-row">
                                <div className="form-group">
                                  <label htmlFor="uploadNik">NIK Karyawan:</label>
                                  <input 
                                    type="text" 
                                    id="uploadNik" 
                                    className="form-control"
                                    value={uploadNik}
                                    onChange={(e) => setUploadNik(e.target.value)}
                                    required
                                  />
                                </div>
                                
                                <div className="form-group">
                                  <label htmlFor="uploadMonth">Bulan:</label>
                                  <select 
                                    id="uploadMonth" 
                                    className="form-control"
                                    value={uploadMonth}
                                    onChange={(e) => setUploadMonth(e.target.value)}
                                  >
                                    <option value="01">Januari (01)</option>
                                    <option value="02">Februari (02)</option>
                                    <option value="03">Maret (03)</option>
                                    <option value="04">April (04)</option>
                                    <option value="05">Mei (05)</option>
                                    <option value="06">Juni (06)</option>
                                    <option value="07">Juli (07)</option>
                                    <option value="08">Agustus (08)</option>
                                    <option value="09">September (09)</option>
                                    <option value="10">Oktober (10)</option>
                                    <option value="11">November (11)</option>
                                    <option value="12">Desember (12)</option>
                                  </select>
                                </div>
                                
                                <div className="form-group">
                                  <label htmlFor="uploadYear">Tahun:</label>
                                  <select 
                                    id="uploadYear" 
                                    className="form-control"
                                    value={uploadYear}
                                    onChange={(e) => setUploadYear(e.target.value)}
                                  >
                                    {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                                      <option key={year} value={year}>{year}</option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                              
                              <div className="form-group">
                                <label htmlFor="uploadFile">File Slip Gaji (PDF):</label>
                                <input 
                                  type="file" 
                                  id="uploadFile" 
                                  className="form-control"
                                  accept=".pdf"
                                  onChange={handleFileChange}
                                  required
                                />
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="form-row">
                                <div className="form-group">
                                  <label htmlFor="uploadMonth"><strong>Pilih Bulan untuk semua file:</strong></label>
                                  <select 
                                    id="uploadMonth" 
                                    className="form-control"
                                    value={uploadMonth}
                                    onChange={(e) => setUploadMonth(e.target.value)}
                                  >
                                    <option value="01">Januari (01)</option>
                                    <option value="02">Februari (02)</option>
                                    <option value="03">Maret (03)</option>
                                    <option value="04">April (04)</option>
                                    <option value="05">Mei (05)</option>
                                    <option value="06">Juni (06)</option>
                                    <option value="07">Juli (07)</option>
                                    <option value="08">Agustus (08)</option>
                                    <option value="09">September (09)</option>
                                    <option value="10">Oktober (10)</option>
                                    <option value="11">November (11)</option>
                                    <option value="12">Desember (12)</option>
                                  </select>
                                </div>
                                
                                <div className="form-group">
                                  <label htmlFor="uploadYear"><strong>Pilih Tahun untuk semua file:</strong></label>
                                  <select 
                                    id="uploadYear" 
                                    className="form-control"
                                    value={uploadYear}
                                    onChange={(e) => setUploadYear(e.target.value)}
                                  >
                                    {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                                      <option key={year} value={year}>{year}</option>
                                    ))}
                                  </select>
                                </div>
                              </div>

                              <div className="form-group">
                                <label htmlFor="uploadNik">NIK Karyawan (opsional, jika file tidak sesuai format):</label>
                                <input 
                                  type="text" 
                                  id="uploadNik" 
                                  className="form-control"
                                  value={uploadNik}
                                  onChange={(e) => setUploadNik(e.target.value)}
                                  placeholder="Gunakan jika nama file tidak berisi NIK"
                                />
                              </div>
                              
                              <div className="form-group">
                                <label htmlFor="uploadFiles">
                                  <strong>Pilih Beberapa File Slip Gaji (PDF):</strong>
                                </label>
                                <input 
                                  type="file" 
                                  id="uploadFiles" 
                                  className="form-control"
                                  accept=".pdf"
                                  multiple
                                  onChange={handleFilesChange}
                                  required
                                />
                                <small className="form-text text-muted">
                                  Format nama file dapat berupa:
                                  <ul style={{ marginTop: '5px', paddingLeft: '20px' }}>
                                    <li>NIK_BULAN_TAHUN.pdf (contoh: 1234_05_2024.pdf) - bulan dan tahun dari nama file akan digunakan</li>
                                    <li>NIK.pdf (contoh: 10016.pdf) - akan menggunakan bulan dan tahun yang dipilih di atas</li>
                                  </ul>
                                </small>
                              </div>
                            </>
                          )}
                          
                          <div className="form-row">
                            <div className="form-group">
                              <label htmlFor="updateExistingUsers">Penanganan User Baru:</label>
                              <select 
                                id="updateExistingUsers" 
                                className="form-control"
                                value={updateExistingUsers}
                                onChange={(e) => setUpdateExistingUsers(e.target.value)}
                              >
                                <option value="preserve">Pertahankan data user yang sudah ada</option>
                                <option value="update">Perbarui data user jika ada perubahan</option>
                              </select>
                            </div>
                            
                            <div className="form-group">
                              <label htmlFor="handleDuplicates">Penanganan Duplikat:</label>
                              <select 
                                id="handleDuplicates" 
                                className="form-control"
                                value={handleDuplicates}
                                onChange={(e) => setHandleDuplicates(e.target.value)}
                              >
                                <option value="skip">Lewati jika file sudah ada</option>
                                <option value="replace">Ganti file yang sudah ada</option>
                              </select>
                            </div>
                          </div>
                          
                          {uploadMessage && (
                            <div className={`alert ${uploadStatus === 'success' ? 'alert-success' : uploadStatus === 'error' ? 'alert-danger' : 'alert-info'}`}>
                              {uploadMessage}
                            </div>
                          )}
                          
                          <div className="form-actions">
                            <button 
                              type="submit" 
                              className="btn-primary"
                              disabled={isUploading}
                            >
                              {isUploading ? 'Mengunggah...' : 'Unggah Slip Gaji'}
                            </button>
                            <button
                              type="button"
                              className="btn-secondary"
                              onClick={() => {
                                setUploadFile(null);
                                setUploadFiles([]);
                                setUploadMessage('');
                                setUploadStatus('');
                                setUploadResult(null);
                                
                                // Reset file inputs
                                const singleFileInput = document.getElementById('uploadFile');
                                const multipleFileInput = document.getElementById('uploadFiles');
                                if (singleFileInput) singleFileInput.value = '';
                                if (multipleFileInput) multipleFileInput.value = '';
                              }}
                              disabled={isUploading}
                              style={{ marginLeft: '10px' }}
                            >
                              Reset
                            </button>
                          </div>
                        </form>
                      </div>
                      
                      {uploadStatus === 'success' && uploadType === 'single' && uploadResult && (
                        <div className="upload-result">
                          <h4>Slip Gaji Berhasil Diunggah</h4>
                          <div className="result-details">
                            <p><strong>NIK:</strong> {uploadResult.nik || uploadNik}</p>
                            <p><strong>Bulan/Tahun:</strong> {uploadResult.month || uploadMonth}/{uploadResult.year || uploadYear}</p>
                            {uploadResult.path && <p><strong>File Path:</strong> {uploadResult.path}</p>}
                            
                            {uploadResult.user_created && (
                              <div className="user-created-info">
                                <p><strong>User Baru Dibuat:</strong></p>
                                <p>NIK: {uploadResult.nik}</p>
                                <p>Password awal: {uploadResult.nik}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {uploadStatus === 'success' && uploadType === 'multiple' && uploadResult && (
                        <div className="upload-result">
                          <h4>Hasil Unggah Multiple</h4>
                          <div className="result-summary">
                            <p>Total: {uploadResult.total} file</p>
                            <p className="success-text">Berhasil: {uploadResult.successful} file</p>
                            {uploadResult.failed > 0 && (
                              <p className="error-text">Gagal: {uploadResult.failed} file</p>
                            )}
                          </div>
                          
                          {uploadResult.details && uploadResult.details.length > 0 && (
                            <div className="result-details">
                              <h5>Detail:</h5>
                              <ul className="upload-details-list">
                                {uploadResult.details.map((detail, index) => (
                                  <li 
                                    key={index} 
                                    className={`upload-detail-item ${detail.status === 'success' ? 'success' : 'error'}`}
                                  >
                                    <div className="detail-file">{detail.file}</div>
                                    {detail.status === 'success' ? (
                                      <div className="detail-info">
                                        <span className="success-badge">Berhasil</span> NIK: {detail.nik}, Bulan/Tahun: {detail.month}/{detail.year}
                                      </div>
                                    ) : (
                                      <div className="detail-error">{detail.message}</div>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {activeTab === 'reports' && (
                <div className="reports">
                  <h2>Laporan</h2>
                  <p>Fitur ini masih dalam pengembangan.</p>
                </div>
              )}
              
              {activeTab === 'settings' && (
                <div className="settings">
                  <h2>Pengaturan</h2>
                  <p>Fitur ini masih dalam pengembangan.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <footer className="dashboard-footer">
        <div className="container">
          <p>&copy; {new Date().getFullYear()} Sistem Penggajian. All rights reserved.</p>
        </div>
      </footer>

      {/* Floating upload button */}
      <div 
        className="floating-upload-btn"
        onClick={() => {
          setActiveTab('salary');
          setActiveSubTab('upload');
          setUploadTabActive(true);
        }}
        title="Unggah Slip Gaji"
      >
        ⬆️
      </div>
    </div>
  );
}

export default AdminPanel; 